/**
 * @file Parent close preflight modal composition.
 */

import { openConfirmationModal } from "../ui/confirmationModal.js";
import { createSelectControl } from "../ui/controls/selectControl.js";
import {
    NATIVE_INPUT_CLASSES,
    bindNativeFieldBehavior,
} from "../forms/nativeFieldStyles.js";
import { sanitizeHTML } from "../utils/contentUtils.js";
import { formatCurrency } from "../utils/displayUtils.js";

const MODE_COPY = {
    close: {
        title: "Close Linked Items?",
        confirmText: "Close All",
        loadingText: "Closing...",
        errorText: "Could not close linked items.",
        statusPrefix: "Closed status",
        statusUnavailableText: "No configured closed status is available for this linked group.",
        linkedAction: "close",
        linkedActionPast: "closed",
        showCloseBlockers: true,
        body: "You are closing",
        blockedBody: "You cannot close",
    },
    hold: {
        title: "Put Linked Items On Hold?",
        confirmText: "Put All On Hold",
        loadingText: "Applying...",
        errorText: "Could not put linked items on hold.",
        statusPrefix: "On-hold status",
        statusUnavailableText: "No configured on-hold status is available for this linked group.",
        linkedAction: "put on hold",
        linkedActionPast: "put on hold",
        showCloseBlockers: false,
        body: "You are putting",
        blockedBody: "You cannot put",
    },
};

function getModeCopy(mode) {
    return MODE_COPY[mode] || MODE_COPY.close;
}

function normalizeCurrencyInput(value) {
    if (value === null || value === undefined || value === "") {return null;}
    const number = typeof value === "number" ?
        value :
        Number(String(value).replace(/[$,\s]/g, ""));
    return Number.isFinite(number) ? number : null;
}

function labNeedsCascadeCost(group = {}, item = {}, selectedStatus = "") {
    return group.itemType === "lab" &&
        group.stream === "external" &&
        item.stream === "external" &&
        selectedStatus === group.closeInterceptStatusId &&
        normalizeCurrencyInput(item.cost) === null;
}

function bindCurrencyInput(input) {
    if (!input || input.dataset.parentCloseCurrencyBound === "true") {return;}
    input.dataset.parentCloseCurrencyBound = "true";
    input.addEventListener("input", (event) => {
        let value = event.target.value.replace(/[^0-9.]/g, "");
        if (value.split(".").length > 2) {
            value = value.substring(0, value.lastIndexOf("."));
        }
        input.dataset.currencyRawValue = value;
        event.target.value = value;
    });
    input.addEventListener("focus", () => {
        input.value = input.dataset.currencyRawValue || "";
    });
    input.addEventListener("blur", () => {
        const rawValue = input.dataset.currencyRawValue || "";
        input.value = formatCurrency(rawValue) || rawValue || "";
    });
}

function getBlockerFieldLabel(blocker = {}) {
    const field = String(blocker.field || "").toLowerCase();
    if (field === "technician") {return "Technician";}
    if (field === "cost") {return "Cost";}
    if (field === "completed_on") {return "Completed On";}
    if (field.startsWith("repair_details.")) {
        return String(blocker.message || "")
            .replace(/\s+is required before closing this Maintenance issue\.$/i, "")
            .trim() || "Required repair field";
    }
    if (field === "repair_details") {return "Repair Details";}
    return String(blocker.message || "Required field")
        .replace(/\s+is required before closing this Maintenance item\.$/i, "")
        .trim();
}

function renderGroupRows(group, copy) {
    return (group.items || [])
        .map((item) => {
            const blockers = copy.showCloseBlockers &&
                Array.isArray(item.closeBlockers) ?
                item.closeBlockers :
                [];
            const blockerHtml = blockers.length > 0 ? `
                <div class="mt-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-950/40 dark:text-gray-300">
                    <div class="font-medium text-gray-700 dark:text-gray-200">The following fields are required before closing:</div>
                    <ul class="mt-1 list-disc space-y-0.5 pl-4">
                        ${blockers.map((blocker) => `
                            <li>${sanitizeHTML(getBlockerFieldLabel(blocker))}</li>
                        `).join("")}
                    </ul>
                </div>
            ` : "";
            const costHtml = copy.showCloseBlockers &&
                group.itemType === "lab" &&
                group.stream === "external" &&
                item.stream === "external" &&
                normalizeCurrencyInput(item.cost) === null ?
                `
                    <div
                        class="mt-3 hidden"
                        data-parent-close-lab-cost-row="${sanitizeHTML(item.id)}"
                        data-parent-close-lab-cost-group="${sanitizeHTML(group.id)}"
                    >
                        <label class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300" for="parent-close-lab-cost-${sanitizeHTML(item.id)}">Cost</label>
                        <input
                            id="parent-close-lab-cost-${sanitizeHTML(item.id)}"
                            type="text"
                            class="${NATIVE_INPUT_CLASSES} currency-input"
                            placeholder="$"
                            inputmode="decimal"
                            data-parent-close-lab-cost="${sanitizeHTML(item.id)}"
                            data-parent-close-lab-cost-group="${sanitizeHTML(group.id)}"
                            data-currency-raw-value=""
                        >
                    </div>
                ` :
                "";
            return `
            <li class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900/40">
                <div class="font-medium text-gray-900 dark:text-gray-100">${sanitizeHTML(item.title || "Untitled")}</div>
                <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">Current status: ${sanitizeHTML(item.statusLabel || "Unknown")}</div>
                ${blockerHtml}
                ${costHtml}
            </li>
        `;
        })
        .join("");
}

function renderGroupControl(group, copy) {
    const isRequired = group.required === true;
    const isBlocked = group.blocked === true;
    const hasStatuses = (group.allowedStatuses || []).length > 0;
    const blockedReason = group.blockedReason ||
        "Close these linked items before closing this parent.";
    const heading = `${sanitizeHTML(group.label || "Linked items")}${isBlocked ? '<span class="ml-1 text-red-600">*</span>' : ""}`;
    return `
        <div class="mt-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div class="flex items-start justify-between gap-3">
                <div>
                    <div class="flex flex-wrap items-center gap-2">
                        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">${heading}</h3>
                        ${isRequired && !isBlocked ? `<span class="text-red-600">*</span>` : ""}
                    </div>
                    ${isBlocked ? "" : `<p class="mt-1 text-sm text-gray-600 dark:text-gray-400">${group.items.length} open linked item(s) ${isRequired ? `must be ${copy.linkedActionPast} before this parent can ${copy.linkedAction}.` : `can be ${copy.linkedActionPast} with this parent.`}</p>`}
                </div>
            </div>
            ${isBlocked ? `
                <div class="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    ${sanitizeHTML(blockedReason)}
                </div>
            ` : ""}
            <ul class="mt-3 max-h-48 space-y-2 overflow-auto pr-1">
                ${renderGroupRows(group, copy)}
            </ul>
            ${hasStatuses ? `<div class="mt-4">
                <label class="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">${copy.statusPrefix} for ${sanitizeHTML(group.label || "items")}</label>
                <input type="hidden" data-parent-close-group-input="${sanitizeHTML(group.id)}" value="${sanitizeHTML(group.defaultStatusId || "")}">
                <div data-parent-close-group-control="${sanitizeHTML(group.id)}"></div>
            </div>` : `
                ${isBlocked ? "" : `
                    <div class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        ${sanitizeHTML(copy.statusUnavailableText)}
                    </div>
                `}
                <input type="hidden" data-parent-close-group-input="${sanitizeHTML(group.id)}" value="">
            `}
        </div>
    `;
}

function mountGroupControls(groups = [], copy = MODE_COPY.close) {
    groups.forEach((group) => {
        const root = document.querySelector(
            `[data-parent-close-group-control="${CSS.escape(group.id)}"]`,
        );
        const mirrorInput = document.querySelector(
            `[data-parent-close-group-input="${CSS.escape(group.id)}"]`,
        );
        if (!root || !mirrorInput) {return;}

        createSelectControl({
            root,
            name: `parentClose.${group.id}`,
            options: (group.allowedStatuses || []).map((status) => ({
                value: status.id,
                label: status.name,
                accentColor: status.color || "",
            })),
            value: group.defaultStatusId || "",
            clearable: false,
            searchable: (group.allowedStatuses || []).length > 6,
            mirrorInput,
            portalMenu: true,
            menuBoundary: root.closest("#confirm-message"),
            accessibleName: `${copy.statusPrefix} for ${group.label || "linked items"}`,
        });
    });
}

function updateLabCostRows(groups = []) {
    groups
        .filter((group) => group.itemType === "lab")
        .forEach((group) => {
            const selectedStatus = document.querySelector(
                `[data-parent-close-group-input="${CSS.escape(group.id)}"]`,
            )?.value || group.defaultStatusId || "";
            (group.items || []).forEach((item) => {
                const row = document.querySelector(
                    `[data-parent-close-lab-cost-row="${CSS.escape(item.id)}"]`,
                );
                const input = document.querySelector(
                    `[data-parent-close-lab-cost="${CSS.escape(item.id)}"]`,
                );
                if (!row || !input) {return;}
                const required = labNeedsCascadeCost(group, item, selectedStatus);
                row.classList.toggle("hidden", !required);
                input.disabled = !required;
                if (!required) {
                    input.value = "";
                    input.dataset.currencyRawValue = "";
                    input.setCustomValidity("");
                }
            });
        });
}

function bindLabCostInputs(groups = []) {
    document
        .querySelectorAll("[data-parent-close-lab-cost]")
        .forEach((input) => {
            bindCurrencyInput(input);
            input.addEventListener("input", () => input.setCustomValidity(""));
        });
    groups
        .filter((group) => group.itemType === "lab")
        .forEach((group) => {
            document
                .querySelector(
                    `[data-parent-close-group-input="${CSS.escape(group.id)}"]`,
                )
                ?.addEventListener("change", () => updateLabCostRows(groups));
        });
    updateLabCostRows(groups);
}

function getSelections(groups = []) {
    return groups.reduce((acc, group) => {
        const input = document.querySelector(
            `[data-parent-close-group-input="${CSS.escape(group.id)}"]`,
        );
        acc[group.id] = input?.value || group.defaultStatusId || "";
        return acc;
    }, {});
}

function setPreflightError(message) {
    const element = document.querySelector("[data-parent-close-error]");
    if (!element) {return;}
    if (!message) {
        element.classList.add("hidden");
        element.textContent = "";
        return;
    }
    element.classList.remove("hidden");
    element.textContent = message;
}

function getLabCompletionDataById(groups = [], selections = {}) {
    const dataById = {};
    let firstInvalid = null;
    for (const group of groups) {
        if (group.itemType !== "lab") {continue;}
        const selectedStatus = selections[group.id] || group.defaultStatusId || "";
        for (const item of group.items || []) {
            if (!labNeedsCascadeCost(group, item, selectedStatus)) {continue;}
            const input = document.querySelector(
                `[data-parent-close-lab-cost="${CSS.escape(item.id)}"]`,
            );
            const cost = input?.dataset?.currencyRawValue ||
                String(normalizeCurrencyInput(input?.value) ?? "");
            if (normalizeCurrencyInput(cost) === null) {
                input?.setCustomValidity("Enter a cost before closing this Lab Item.");
                firstInvalid = firstInvalid || input;
                continue;
            }
            input?.setCustomValidity("");
            dataById[item.id] = {cost};
        }
    }
    if (firstInvalid) {
        bindNativeFieldBehavior(firstInvalid.closest("[data-parent-close-error]") ||
            document);
        firstInvalid.reportValidity?.();
        firstInvalid.focus?.({preventScroll: true});
        firstInvalid.scrollIntoView?.({block: "center", behavior: "smooth"});
        return {valid: false, dataById: {}};
    }
    return {valid: true, dataById};
}

async function runPreflightAction(action, payload, fallbackError) {
    try {
        setPreflightError("");
        await action(payload);
    } catch (error) {
        setPreflightError(error?.message || fallbackError);
        throw error;
    }
}

/**
 * Opens the parent close preflight modal.
 * @param {object} params Modal params.
 * @param {object} params.preflight Backend preflight response.
 * @param {Function} params.onApply Applies parent close with cascades.
 * @param {Function} params.onCancel Handles cancelling the close.
 * @param {Function} params.onSkip Applies parent close without cascades.
 * @returns {void}
 */
export function openParentClosePreflightModal({
    onCancel,
    preflight,
    onApply,
    onSkip,
}) {
    const groups = preflight?.groups || [];
    const mode = preflight?.mode === "hold" ? "hold" : "close";
    const copy = getModeCopy(mode);
    const hasRequiredGroups = groups.some((group) => group.required === true);
    const hasBlockingGroups = groups.some((group) => group.blocked === true);
    const parentTitle = preflight?.parent?.title || "this item";
    const bodyHtml = `
        <div class="text-sm text-gray-600 dark:text-gray-300">
            <div data-parent-close-error class="hidden rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"></div>
            <p>${hasBlockingGroups ?
                `${copy.blockedBody} <strong>${sanitizeHTML(parentTitle)}</strong>${mode === "hold" ? " on hold" : ""} until the blocked linked items are resolved.` :
                `${copy.body} <strong>${sanitizeHTML(parentTitle)}</strong>${mode === "hold" ? " on hold" : ""}. Choose whether to ${copy.linkedAction} linked items at the same time.`}</p>
            ${groups.map((group) => renderGroupControl(group, copy)).join("")}
        </div>
    `;

    openConfirmationModal(
        copy.title,
        bodyHtml,
        async () => {
            if (hasBlockingGroups) {
                onCancel?.();
                return true;
            }
            const selections = getSelections(groups);
            const labCompletionData = getLabCompletionDataById(groups, selections);
            if (!labCompletionData.valid) {return false;}
            await runPreflightAction(onApply, {
                cascade: groups.reduce((acc, group) => {
                    acc[group.id] = true;
                    return acc;
                }, {}),
                labCompletionDataById: labCompletionData.dataById,
                selections,
            }, copy.errorText);
        },
        false,
        hasBlockingGroups ? "OK" : copy.confirmText,
        onCancel,
        copy.loadingText,
        "Cancel",
        {
            closeOnError: false,
            keepOpenOnFalse: true,
            leftAction: hasRequiredGroups || hasBlockingGroups ? null : {
                buttonText: "Parent Only",
                danger: false,
                loadingText: copy.loadingText,
                onClick: async () => {
                    await runPreflightAction(onSkip, {
                        cascade: groups.reduce((acc, group) => {
                            acc[group.id] = false;
                            return acc;
                        }, {}),
                        selections: {},
                    }, copy.errorText);
                },
                placement: "beforeConfirm",
                variant: "secondary",
            },
            scrollBody: true,
        },
    );

    mountGroupControls(groups, copy);
    bindLabCostInputs(groups);
}
